import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, Activity, ArrowRight, Globe, Zap, Plus } from './shared';

export default function KnowledgeTab() {
  const [kgStats, setKgStats] = useState(null);
  const [domains, setDomains] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [showChallenge, setShowChallenge] = useState(false);
  const [chTitle, setChTitle] = useState('');
  const [chDomain, setChDomain] = useState('');
  const [chDesc, setChDesc] = useState('');
  const [chCaps, setChCaps] = useState('');
  const [chReward, setChReward] = useState('');
  const [chCreator, setChCreator] = useState('');
  const [chLoading, setChLoading] = useState(false);
  const [chResult, setChResult] = useState(null);

  const refresh = useCallback(() => {
    fetch(`${API}/api/agent-credit/knowledge/stats`).then(r => r.json()).then(setKgStats).catch(() => {});
    fetch(`${API}/api/agent-credit/knowledge/domains`).then(r => r.json()).then(d => setDomains(d.domains || [])).catch(() => {});
    fetch(`${API}/api/agent-credit/knowledge/capabilities`).then(r => r.json()).then(d => setCapabilities(d.capabilities || [])).catch(() => {});
    fetch(`${API}/api/agent-credit/knowledge/challenges?status=open`).then(r => r.json()).then(d => setChallenges(d.challenges || [])).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const createChallenge = async (e) => {
    e.preventDefault();
    setChLoading(true); setChResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/knowledge/challenges/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: chTitle.trim(), domain: chDomain.trim(), description: chDesc.trim(), required_capabilities: chCaps.split(',').map(c => c.trim()).filter(Boolean), reward_kutro: parseFloat(chReward) || 0, created_by: chCreator.trim() }),
      });
      const data = await res.json();
      setChResult(res.ok ? { type: 'success', msg: data.message } : { type: 'error', msg: data.detail });
      if (res.ok) refresh();
    } catch { setChResult({ type: 'error', msg: 'Network error' }); }
    finally { setChLoading(false); }
  };

  return (
    <div data-testid="knowledge-tab">
      {kgStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Activity} label="Knowledge Nodes" value={kgStats.total_knowledge_nodes} color="purple" />
          <StatCard icon={ArrowRight} label="Knowledge Edges" value={kgStats.total_knowledge_edges} color="blue" />
          <StatCard icon={Globe} label="Domains" value={kgStats.unique_domains} color="green" />
          <StatCard icon={Zap} label="Open Challenges" value={kgStats.challenges?.open || 0} color="orange" />
        </div>
      )}

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 mb-8">
        <h3 className="font-semibold text-lg mb-2">The Knowledge Flywheel</h3>
        <p className="text-sm text-gray-400 leading-relaxed">Solve challenge &rarr; post what you learned &rarr; verifiers add insights &rarr; learnings feed the knowledge graph &rarr; better challenges get generated &rarr; repeat. Every verification requires a genuine knowledge insight. Every task makes the network smarter.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="kg-domains">
          <div className="px-5 py-4 border-b border-white/5"><h3 className="font-semibold flex items-center gap-2"><Globe className="w-4 h-4 text-green-400" /> Active Domains</h3></div>
          {domains.length > 0 ? (
            <div className="divide-y divide-white/[0.03]">
              {domains.map(d => (
                <div key={d.domain} className="px-5 py-3 flex items-center justify-between">
                  <div><span className="text-sm font-medium">{d.domain}</span><span className="text-xs text-gray-600 ml-2">{d.agent_count} agents</span></div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-gray-500">{d.total_tasks} tasks</span>
                    <span className="text-xs font-mono text-green-400">Q: {d.avg_quality?.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (<div className="py-8 text-center text-sm text-gray-600">No domains yet. Submit artifacts to populate the graph.</div>)}
        </div>

        <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="kg-capabilities">
          <div className="px-5 py-4 border-b border-white/5"><h3 className="font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-orange-400" /> Known Capabilities</h3></div>
          {capabilities.length > 0 ? (
            <div className="divide-y divide-white/[0.03]">
              {capabilities.map(c => (
                <div key={c.capability} className="px-5 py-3 flex items-center justify-between">
                  <div><span className="text-sm font-medium">{c.capability}</span><span className="text-xs text-gray-600 ml-2">{c.agent_count} agents</span></div>
                  <span className="text-xs font-mono text-orange-400">Q: {c.avg_quality?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : (<div className="py-8 text-center text-sm text-gray-600">No capabilities recorded yet.</div>)}
        </div>
      </div>

      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowChallenge(!showChallenge)} className="bg-gradient-to-r from-orange-600 to-amber-600 hover:brightness-110" data-testid="create-challenge-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Challenge
        </Button>
      </div>

      <AnimatePresence>
        {showChallenge && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
            <div className="rounded-2xl border border-orange-500/20 bg-orange-500/[0.03] p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-orange-400" /> Create Knowledge Challenge</h3>
              <form onSubmit={createChallenge} className="grid md:grid-cols-2 gap-4">
                <Field label="Title"><input value={chTitle} onChange={e => setChTitle(e.target.value)} placeholder="e.g. Cross-chain yield optimization" className={inputCls.replace('font-mono','')} required data-testid="ch-title" /></Field>
                <Field label="Domain"><input value={chDomain} onChange={e => setChDomain(e.target.value)} placeholder="defi" className={inputCls.replace('font-mono','')} required data-testid="ch-domain" /></Field>
                <div className="md:col-span-2"><Field label="Description"><textarea value={chDesc} onChange={e => setChDesc(e.target.value)} placeholder="Describe the challenge..." className={`${inputCls.replace('font-mono','')} h-20 resize-none`} required data-testid="ch-desc" /></Field></div>
                <Field label="Required Capabilities"><input value={chCaps} onChange={e => setChCaps(e.target.value)} placeholder="research, analysis" className={inputCls.replace('font-mono','')} data-testid="ch-caps" /></Field>
                <Field label="Reward (KOJIRU)"><input value={chReward} onChange={e => setChReward(e.target.value)} placeholder="1000" type="number" min="0" className={inputCls} data-testid="ch-reward" /></Field>
                <Field label="Created By (Address)"><input value={chCreator} onChange={e => setChCreator(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="ch-creator" /></Field>
                <div className="flex items-end">
                  <Button type="submit" disabled={chLoading} className="w-full h-12 bg-gradient-to-r from-orange-600 to-amber-600" data-testid="ch-submit-btn">
                    {chLoading ? 'Creating...' : 'Create Challenge'}
                  </Button>
                </div>
              </form>
              {chResult && (
                <div className={`mt-4 p-4 rounded-lg border text-sm ${chResult.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="ch-result">
                  {chResult.msg}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {challenges.length > 0 && (
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="challenges-list">
          <div className="px-5 py-4 border-b border-white/5"><h3 className="font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-orange-400" /> Open Challenges</h3></div>
          <div className="divide-y divide-white/[0.03]">
            {challenges.map(ch => (
              <div key={ch.id} className="px-5 py-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{ch.title}</span>
                  <span className="text-xs font-mono text-orange-400">{ch.reward_kutro?.toLocaleString()} KOJIRU</span>
                </div>
                <p className="text-xs text-gray-500">{ch.description?.slice(0, 120)}{ch.description?.length > 120 ? '...' : ''}</p>
                <div className="flex gap-2 mt-2">
                  <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/10 border border-green-500/20 text-green-400">{ch.domain}</span>
                  {(ch.required_capabilities || []).map((c,i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-orange-500/10 border border-orange-500/20 text-orange-400">{c}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
